import { useCallback, useMemo, useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useLanguage } from "@/context/LanguageContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const ALLOWED_TIERS = [5, 20, 100];
const PAYPAL_BUTTON_STYLE = { layout: "vertical", color: "gold", label: "paypal" };

const logClientError = (message, error) => {
  if (process.env.NODE_ENV !== "production") {
    console.error(message, error);
  }
};

const parseAmount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const buildInitErrorMessage = (copy, error) => {
  const detail = error?.response?.data?.detail;
  const detailMessage = detail?.message ? ` ${detail.message}` : "";
  const detailRef = detail?.debug_id ? ` (ref: ${detail.debug_id})` : "";
  return `${copy.donationFlow.initError}${detailMessage}${detailRef}`.trim();
};

const createDonationOrder = async (amount) => {
  const response = await axios.post(`${API}/donate/create-order`, { tier: amount });
  return response.data.id;
};

const captureDonationOrder = async (orderId) => {
  await axios.post(`${API}/donate/capture-order`, { order_id: orderId });
};

const CheckoutHeader = ({ copy }) => (
  <div className="section-header">
    <p className="section-kicker" data-testid="donation-checkout-kicker">
      {copy.donation.kicker}
    </p>
    <h1 className="section-title" data-testid="donation-checkout-title">
      {copy.donationFlow.checkoutTitle}
    </h1>
    <p className="section-text" data-testid="donation-checkout-body">
      {copy.donationFlow.checkoutBody}
    </p>
  </div>
);

const InvalidAmountNotice = ({ copy }) => (
  <div className="donation-alert" data-testid="donation-invalid">
    {copy.donationFlow.invalidAmount}
    <Link to="/" className="donation-link" data-testid="donation-back-home">
      {copy.donationFlow.backHome}
    </Link>
  </div>
);

const DonationAmount = ({ amount, label }) => (
  <div className="donation-amount" data-testid="donation-amount">
    <span className="donation-amount-label" data-testid="donation-amount-label">
      {label}
    </span>
    <span className="donation-amount-value" data-testid="donation-amount-value">
      ${amount}
    </span>
  </div>
);

const useDonationCheckout = () => {
  const { copy } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");

  const amountParam = searchParams.get("amount");
  const amount = useMemo(() => parseAmount(amountParam), [amountParam, parseAmount]);
  const isValidAmount = amount !== null && ALLOWED_TIERS.includes(amount);

  const paypalOptions = useMemo(
    () => ({ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }),
    [PAYPAL_CLIENT_ID],
  );

  const createOrder = useCallback(async () => {
    try {
      return await createDonationOrder(amount);
    } catch (error) {
      logClientError("PayPal order creation failed", error);
      const fallbackMessage = buildInitErrorMessage(copy, error);
      setErrorMessage(fallbackMessage || copy.donationFlow.initError);
      throw error;
    }
  }, [amount, copy, createDonationOrder, buildInitErrorMessage, logClientError, setErrorMessage]);

  const onApprove = useCallback(
    async (data) => {
      try {
        await captureDonationOrder(data.orderID);
        navigate(`/donation-success?order=${data.orderID}`);
      } catch (error) {
        logClientError("PayPal capture failed", error);
        setErrorMessage(copy.donationFlow.captureError);
      }
    },
    [captureDonationOrder, copy, logClientError, navigate, setErrorMessage],
  );

  const onCancel = useCallback(() => {
    navigate("/donation-cancel");
  }, [navigate]);

  return {
    copy,
    amount,
    isValidAmount,
    errorMessage,
    paypalOptions,
    createOrder,
    onApprove,
    onCancel,
  };
};

const DonationCheckout = () => {
  const { copy, amount, isValidAmount, errorMessage, paypalOptions, createOrder, onApprove, onCancel } =
    useDonationCheckout();

  return (
    <div className="donation-page" data-testid="donation-checkout-page">
      <div className="donation-card" data-testid="donation-checkout-card">
        <CheckoutHeader copy={copy} />

        {!isValidAmount ? (
          <InvalidAmountNotice copy={copy} />
        ) : (
          <div className="donation-checkout" data-testid="donation-checkout">
            <DonationAmount amount={amount} label={copy.donation.recommended} />
            {errorMessage ? (
              <p className="donation-error" data-testid="donation-error">
                {errorMessage}
              </p>
            ) : null}
            {PAYPAL_CLIENT_ID ? (
              <PayPalScriptProvider options={paypalOptions}>
                <div data-testid="donation-paypal-buttons">
                  <PayPalButtons
                    style={PAYPAL_BUTTON_STYLE}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onCancel={onCancel}
                  />
                </div>
              </PayPalScriptProvider>
            ) : (
              <p className="donation-error" data-testid="donation-paypal-missing">
                {copy.donationFlow.missingClientId}
              </p>
            )}
            <Link to="/" className="donation-link" data-testid="donation-back-home">
              {copy.donationFlow.backHome}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonationCheckout;
